<script setup lang="ts">
import AppAlert from "@/shared/components/AppAlert.vue";
import AppModal from "@/shared/components/AppModal.vue";
import BrowserCheckTip from "@/shared/components/BrowserCheckTip.vue";
import { eventBus } from "@/shared/events";
import { useEventBus } from "@/shared/utils/eventBus";
import { useToast } from "@nuxt/ui/composables";
import { promiseTimeout, useConfirmDialog } from "@vueuse/core";
import { computed, provide, ref } from "vue";

const toast = useToast();
const showAlert = ref(false);
const alertTitle = ref("");
const alertMessage = ref("");
const alertConfirmText = ref("");
const alertColor = ref<"primary" | "success" | "warning" | "error" | "neutral">("primary");
const alertPosition = ref<"center" | "bottom">("center");
const alertDuration = ref(2000);

const alertBus = useEventBus();
alertBus.on("alert", (payload) => {
  alertTitle.value = payload.title || "提示";
  alertMessage.value = payload.message || "";
  alertConfirmText.value = payload.confirmText || "确定";
  alertColor.value = payload.color || "primary";
  if (payload.position) alertPosition.value = payload.position;
  if (payload.duration) alertDuration.value = payload.duration;
  showAlert.value = true;
});

eventBus.on("network:offline", () => {
  toast.add({ title: "网络异常", description: "请检查您的网络连接", color: "error" });
});
eventBus.on("network:timeout", ({ url, timeout }) => {
  toast.add({
    title: "请求超时",
    description: `请求 ${url} 超时 (${timeout}ms)`,
    color: "warning",
  });
});

const {
  isRevealed,
  reveal: revealConfirm,
  confirm: confirmDialog,
  cancel: cancelDialog,
} = useConfirmDialog();
const showConfirm = computed({
  get: () => isRevealed.value,
  set: (value: boolean) => {
    if (!value) cancelDialog();
  },
});
const confirmMessage = ref("");
let queue: Promise<unknown> = Promise.resolve();

async function askConfirm(message: string): Promise<boolean> {
  const previous = queue;
  const { promise: barrier, resolve: release } = Promise.withResolvers<void>();
  queue = barrier;
  try {
    await previous;
  } catch {
    // A failed confirmation must not block the next one.
  }
  try {
    confirmMessage.value = message;
    const { isCanceled } = await revealConfirm();
    await promiseTimeout(200);
    return !isCanceled;
  } finally {
    release();
  }
}
provide("confirm", askConfirm);
</script>

<template>
  <UApp>
    <RouterView />
    <AppAlert
      v-model:open="showAlert"
      :title="alertTitle"
      :message="alertMessage"
      :confirm-text="alertConfirmText"
      :color="alertColor"
      :position="alertPosition"
      :duration="alertDuration" />
    <AppModal
      v-if="showConfirm"
      v-model:open="showConfirm"
      title="确认操作">
      <p class="text-sm text-default">{{ confirmMessage }}</p>
      <template #footer>
        <UButton
          variant="ghost"
          color="neutral"
          @click="void cancelDialog()">
          取消
        </UButton>
        <UButton
          color="primary"
          @click="void confirmDialog()">
          确认
        </UButton>
      </template>
    </AppModal>
  </UApp>
  <BrowserCheckTip />
</template>

<style>
[data-popper-placement],
[role="listbox"] {
  z-index: 9999 !important;
}
</style>
